/**
 * One sub-job in the Job Status table: a summary row that expands into a
 * tabbed panel.
 *
 * The row alone answers "where is this batch" — qty, promise date, progress,
 * age. That is what people scan the table for, so it stays a single compact
 * line no matter how much history the sub-job carries. Everything deeper
 * lives behind the expander, split into two tabs because it answers two
 * different questions:
 *
 *   Details — what is being made (part, drawing, route, flags, dates)
 *   Audit   — who changed it and when (the ERP event trail)
 *
 * Previously the full audit trail rendered inline under every row, so a
 * five-sub-job line opened as hundreds of rows of history and the summary
 * table it belonged to scrolled off the top.
 */
import { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Cog,
  PackageCheck,
  type LucideIcon,
} from 'lucide-react';
import type { JobTrack } from './jobTrack';
import JobEventTimeline from './JobEventTimeline';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useFormatters } from '../../contexts/PreferencesContext';
import { shortJobNumber, relativeAge } from './jobStatusParsing';

interface JobSubJobRowProps {
  /** The sub-job to render. */
  track: JobTrack;
}

type StageState = 'done' | 'current' | 'todo';

function pickStageState(stageReached: boolean, isLatest: boolean): StageState {
  if (!stageReached) return 'todo';
  if (isLatest) return 'current';
  return 'done';
}

/** Compact 2-dot stepper used inside each sub-job row. */
function MiniStepper({ inProcess, dispatched, latestStage }: {
  inProcess: boolean;
  dispatched: boolean;
  latestStage: 'inProcess' | 'dispatched' | null;
}) {
  const inProcessState = pickStageState(inProcess, latestStage === 'inProcess');
  const dispatchedState = pickStageState(dispatched, latestStage === 'dispatched');
  return (
    <div className="flex items-center gap-1.5" aria-label="Job progress">
      <Dot state={inProcessState} icon={Cog} title="In process" />
      <div className={`h-0.5 w-10 rounded ${dispatched ? 'bg-emerald-500' : 'bg-slate-200'}`} aria-hidden="true" />
      <Dot state={dispatchedState} icon={PackageCheck} title="Dispatched" />
    </div>
  );
}

function Dot({ state, icon: Icon, title }: { state: StageState; icon: LucideIcon; title: string }) {
  const isDone = state === 'done';
  const isCurrent = state === 'current';
  const ringClass = isDone || isCurrent ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300';
  return (
    <div className={`relative h-5 w-5 rounded-full border-2 flex items-center justify-center ${ringClass}`} title={title}>
      {isCurrent && (
        <span className="absolute -inset-0.5 rounded-full bg-emerald-400 opacity-60 animate-ping" aria-hidden="true" />
      )}
      {isDone || isCurrent ? (
        <Check className="relative h-3 w-3 text-white" strokeWidth={3} aria-hidden="true" />
      ) : (
        <Icon className="h-2.5 w-2.5 text-slate-400" aria-hidden="true" />
      )}
    </div>
  );
}

/** One label/value pair in the Details grid. Renders a dash when unset. */
function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm truncate ${empty ? 'text-muted-foreground' : 'text-foreground'}`} title={empty ? undefined : String(value)}>
        {empty ? '—' : value}
      </div>
    </div>
  );
}

/** Amber chip for the handling flags that change how a job is treated. */
function Flag({ label }: { label: string }) {
  return (
    <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
      {label}
    </span>
  );
}

/**
 * A sub-job summary row with an expandable Details / Audit panel.
 *
 * @param track - The sub-job, from either the ERP-record or change-log path
 */
export default function JobSubJobRow({ track }: JobSubJobRowProps) {
  const { formatDate } = useFormatters();
  const [expanded, setExpanded] = useState(false);

  // Stage flags, not timestamps: the ERP marks reworks dispatched without
  // ever sending a completion date, and reading the date left those rows
  // showing as still in process.
  const inProcessDone = track.inProcess;
  const dispatchedDone = track.dispatched;
  const latestStage =
    dispatchedDone ? 'dispatched' :
    inProcessDone ? 'inProcess' :
    null;
  const isSplit = track.record?.is_split ?? track.jobNumber.includes('_');
  const record = track.record;

  // Nothing to expand into on the change-log fallback: no record for Details,
  // no events for Audit. Keep the row inert rather than opening an empty panel.
  const canExpand = !!record || track.events.length > 0;

  const flags = record
    ? ([
        record.on_hold && 'On hold',
        record.fair_required && 'FAIR required',
        record.itar && 'ITAR',
        record.concession && 'Concession',
        record.is_rework && 'Rework',
        record.from_ncr && 'From NCR',
      ].filter(Boolean) as string[])
    : [];

  return (
    <div className="border-b last:border-b-0">
      <div
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? expanded : undefined}
        onClick={canExpand ? () => setExpanded(v => !v) : undefined}
        onKeyDown={canExpand ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(v => !v);
          }
        } : undefined}
        className={`grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-4 px-4 py-2.5 items-center ${canExpand ? 'cursor-pointer hover:bg-slate-50' : ''}`}
      >
        <div className="flex items-baseline gap-2 min-w-0">
          {canExpand && (
            expanded
              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" aria-hidden="true" />
              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 self-center" aria-hidden="true" />
          )}
          <span className="font-mono text-sm" title={track.jobNumber}>
            {shortJobNumber(track.jobNumber)}
          </span>
          {isSplit && (
            <span className="text-[10px] uppercase tracking-wide text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">split</span>
          )}
          {track.events.length > 0 && (
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {track.events.length} {track.events.length === 1 ? 'event' : 'events'}
            </span>
          )}
        </div>
        {/* Quantity and promise date are what people scan this table for, so
            they carry more weight than the job number. */}
        <div className="text-base font-bold tabular-nums text-right font-mono">{track.qty}</div>
        <div className="text-base font-bold tabular-nums text-right whitespace-nowrap">
          {track.promiseAt ? formatDate(track.promiseAt) : <span className="text-muted-foreground font-normal">—</span>}
        </div>
        <div className="flex justify-center">
          <MiniStepper inProcess={inProcessDone} dispatched={dispatchedDone} latestStage={latestStage} />
        </div>
        <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap text-right">
          {relativeAge(track.latestAt)}
        </div>
      </div>

      {canExpand && expanded && (
        <div className="px-4 pb-4 bg-slate-50/60">
          <Tabs defaultValue={record ? 'details' : 'audit'}>
            <TabsList className="h-8">
              {record && <TabsTrigger value="details" className="text-xs px-3">Details</TabsTrigger>}
              <TabsTrigger value="audit" className="text-xs px-3">
                Audit{track.events.length > 0 && ` (${track.events.length})`}
              </TabsTrigger>
            </TabsList>

            {record && (
              <TabsContent value="details" className="mt-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                  <Field label="Job number" value={record.job_number} />
                  <Field label="Master job" value={record.master_job_number} />
                  <Field label="Part number" value={record.part_number} />
                  <Field label="Part description" value={record.part_description} />
                  <Field label="Part issue" value={record.part_issue} />
                  <Field label="Drawing" value={record.drawing_number} />
                  <Field label="Drawing issue" value={record.drawing_issue} />
                  <Field label="Route" value={record.route_name || record.route_number} />
                  <Field label="Quantity" value={record.quantity} />
                  <Field label="Status" value={record.status_label} />
                  <Field label="Batch / serial" value={record.batch_serial_no} />
                  <Field label="Material spec" value={record.material_spec} />
                  <Field label="Promise date" value={record.due_date ? formatDate(record.due_date) : null} />
                  <Field label="Customer required" value={record.customer_required_date ? formatDate(record.customer_required_date) : null} />
                  <Field label="Started" value={record.started_at ? formatDate(record.started_at) : null} />
                  <Field label="Completed" value={record.completed_at ? formatDate(record.completed_at) : null} />
                  <Field label="CofC number" value={record.certificate_number} />
                  <Field label="FAIR" value={record.fair_number ? `${record.fair_number} · ${record.fair_status_label}` : null} />
                  <Field label="FAIR approved" value={record.fair_approved_at ? formatDate(record.fair_approved_at) : null} />
                </div>
                {flags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t">
                    {flags.map(f => <Flag key={f} label={f} />)}
                  </div>
                )}
                {record.route_operations.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
                      Process route
                    </div>
                    <ol className="space-y-1">
                      {record.route_operations.map(op => (
                        <li key={op.id} className="flex items-baseline gap-2 text-xs">
                          <span className="font-mono text-muted-foreground tabular-nums w-6 shrink-0">{op.sequence}</span>
                          <span className="font-medium">{op.process_name}</span>
                          {op.specification && (
                            <span className="text-muted-foreground">
                              {op.specification}{op.issue_number && ` · issue ${op.issue_number}`}
                            </span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </TabsContent>
            )}

            <TabsContent value="audit" className="mt-3">
              {track.events.length > 0 ? (
                <JobEventTimeline events={track.events} />
              ) : (
                <p className="text-xs text-muted-foreground py-2">
                  No ERP changes recorded for this sub-job.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
